import { z } from "zod";
import { SUPPORTED_CARRIER_IDS } from "helpers/carriers";
import { noSpecialCharactersRegex } from "./noSpecialCharactersRegex";

const emptyStringToUndefined = (value) =>
  typeof value === "string" && value.trim() === "" ? undefined : value;

const inFormDriverSchema = z
  .object({
    id: z.string().optional(),

    firstName: z
      .string()
      .min(1, "First Name is required")
      .regex(
        noSpecialCharactersRegex,
        "First Name must not contain special characters"
      ),
    lastName: z
      .string()
      .min(1, "Last Name is required")
      .regex(
        noSpecialCharactersRegex,
        "Last Name must not contain special characters"
      ),

    dateOfBirth: z.coerce.date({
      required_error: "Date of Birth is required",
      invalid_type_error: "Invalid date",
    }),

    dateOfHire: z.coerce
      .date({
        invalid_type_error: "Invalid date",
      }).optional(),

    yearsExperience: z.coerce
      .number({
        required_error: "Years of experience is required",
        invalid_type_error: "Must be a number",
      })
      .min(0, "Must be at least 0"),

    licenseState: z.string().min(1, "License State is required"),
    licenseNumber: z.string().min(1, "License Number is required"),

    accidents: z.coerce.number().min(0, "Must be at least 0"),
    violations: z.coerce.number().min(0, "Must be at least 0"),
    suspensions: z.coerce.number().min(0, "Must be at least 0"),

    eligibility: z.string().optional(), // UI-only
    exclude: z.boolean().optional().default(false),
    status: z.enum(["active", "inactive"]).optional(),
  });

export const Step5Schema = z.object({
  carrier: z
    .array(z.enum(SUPPORTED_CARRIER_IDS))
    .default([]),
  wayOfCompensation: z.enum(["Hourly", "Percent Of Load", "Mileage", "By Trip"]).optional(),
  companyRulesProcedures: z.boolean().optional(),
  dailyVehicleInspections: z.boolean().optional(),
  equipmentFamiliarization: z.boolean().optional(),
  routeFamiliarization: z.boolean().optional(),
  emergencyProcedures: z.boolean().optional(),
  accidentReportingProcedures: z.boolean().optional(),
  writtenDriverSafetyManual: z.boolean().optional(),
  mvrsCheckedAnnually: z.boolean().optional(),
  driverHomeDaysPerWeek: z.preprocess(
    emptyStringToUndefined,
    z.coerce.number("Required").min(1, "Must be at least 1").max(7, "Cannot exceed 7").optional()
  ),
  driversDotCompliant: z.boolean().default(true),
  minimumAgeOfDrivers: z.preprocess(
    emptyStringToUndefined,
    z.coerce.number().min(18, "Must be at least 18").optional()
  ),
  violationsPastSixMonths: z.preprocess(
    emptyStringToUndefined,
    z.coerce.number().min(0, "Must be at least 0").optional()
  ),
  zeroTolerancePolicyForMajorViolations: z.boolean().optional(),
  disciplinaryActionForDrivers: z.string().optional(),
  howOftenAreMandatorySafetyMeetingsHeld: z.string().optional(),
  maintainAccidentRegister: z.boolean().optional(),
  writtenMaintenanceProgram: z.boolean().optional(),
  insuredDoTheirOwnMaintenance: z.boolean().optional(),
  safetyRewardsProgram: z.boolean().optional(),
  cdlExperience: z.object({
    year: z.string().optional(),
    month: z.string().optional(),
  }).optional(),
  drivers: z
    .array(inFormDriverSchema)
    .min(1, "At least 1 driver required")
    .default([]),
}).superRefine((data, ctx) => {
  const carriers = (data.carrier ?? []).map((c) => c ?? "");
  const isProgressiveOnly =
    carriers.length === 1 && carriers[0] === "Progressive";
  const minDriverAge = isProgressiveOnly ? 18 : 23;

  (data.drivers ?? []).forEach((driver, idx) => {
    if (!(driver.dateOfBirth instanceof Date)) {
      return;
    }

    const today = new Date();
    const age = today.getFullYear() - driver.dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - driver.dateOfBirth.getMonth();
    const dayDiff = today.getDate() - driver.dateOfBirth.getDate();
    const actualAge =
      monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (Number.isFinite(actualAge) && actualAge < minDriverAge) {
      ctx.addIssue({
        path: ["drivers", idx, "dateOfBirth"],
        code: "custom",
        message: `Driver must be at least ${minDriverAge} years old`,
      });
    }

    if (!isProgressiveOnly && Number.isFinite(driver.yearsExperience)) {
      if (driver.yearsExperience < 2) {
        ctx.addIssue({
          path: ["drivers", idx, "yearsExperience"],
          code: "custom",
          message: "Minimum 2 years of commercial driving experience required",
        });
      }

      const maxExperience = actualAge - 18;
      if (Number.isFinite(maxExperience) && driver.yearsExperience > maxExperience) {
        ctx.addIssue({
          path: ["drivers", idx, "yearsExperience"],
          code: "custom",
          message: `Experience cannot exceed ${maxExperience} years (age - 18)`,
        });
      }
    }
  });

  const hasAccelerant = data.carrier.includes("Accelerant");

  if (hasAccelerant) {
    if (!data.wayOfCompensation) {
      ctx.addIssue({
        code: "custom",
        path: ["wayOfCompensation"],
        message: "Please select a way of compensation",
      });
    }

    if (!data.driverHomeDaysPerWeek && data.driverHomeDaysPerWeek !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["driverHomeDaysPerWeek"],
        message: "Please enter how many days drivers are home per week",
      });
    }
    if (!data.minimumAgeOfDrivers) {
      ctx.addIssue({
        code: "custom",
        path: ["minimumAgeOfDrivers"],
        message: "Please enter the minimum age of drivers",
      });
    }
    if (!data.violationsPastSixMonths && data.violationsPastSixMonths !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["violationsPastSixMonths"],
        message: "Please enter the number of violations past 6 months",
      });
    }
    if (!data.disciplinaryActionForDrivers) {
      ctx.addIssue({
        code: "custom",
        path: ["disciplinaryActionForDrivers"],
        message: "Please enter the disciplinary action for drivers",
      });
    }
    if (!data.howOftenAreMandatorySafetyMeetingsHeld) {
      ctx.addIssue({
        code: "custom",
        path: ["howOftenAreMandatorySafetyMeetingsHeld"],
        message: "Please enter how often mandatory safety meetings are held",
      });
    }
    if (!data.cdlExperience) {
      ctx.addIssue({
        code: "custom",
        path: ["cdlExperience"],
        message: "Please enter the CDL experience",
      });
    } else {
      if (!data.cdlExperience.year || !data.cdlExperience.month) {
        ctx.addIssue({
          code: "custom",
          path: ["cdlExperience"],
          message: "Please enter the CDL experience",
        });
      }
    }
  }
});
